import React, { RefObject, useEffect, useState } from "react";
import { useTranslation } from "next-i18next";
import RatingDiamonds from "components/ui_components/ratingDiamonds";

type RatingModalPropsType = {
  modalRef: RefObject<HTMLDialogElement>;
  closeModal: () => void;
  productReview: number;
  setProductReview: (newRate: number) => void;
  clientServiceReview: number;
  setClientServiceReview: (newRate: number) => void;
  sendReview: (
    payload: Partial<{
      general_review: number;
      customer_service_review: number;
      product_image_review: number;
    }>,
  ) => void;
  setHasSubmitted?: (hasSubmitted: boolean) => void;
};

function RatingModal({
  modalRef,
  closeModal,
  clientServiceReview,
  setClientServiceReview,
  productReview,
  setProductReview,
  sendReview,
  setHasSubmitted,
}: RatingModalPropsType) {
  const { t: translate } = useTranslation("page_detail");
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Store the keydown handler so we can remove it later
  const handleKeyDown = (e) => {
    // Prevent arrow keys, space, page up/down

    if (["ArrowUp", "ArrowDown", "Space", "PageUp", "PageDown"].includes(e.code)) {
      e.preventDefault();
    }
  };
  const preventDefault = (e) => e.preventDefault();

  useEffect(() => {
    const dialog = modalRef.current;
    if (!dialog) return;

    const show = () => {
      if (!dialog.open) {
        try {
          dialog.showModal();
          // document.body.style.overflow = "hidden";
          document.addEventListener("wheel", preventDefault, { passive: false });
          document.addEventListener("touchmove", preventDefault, { passive: false });
          document.addEventListener("keydown", handleKeyDown);

          setIsModalOpen(true);
        } catch (error) {
          console.error("Erreur lors de showModal :", error);
        }
      }
    };

    requestAnimationFrame(show);

    const handleOutsideClick = (event: MouseEvent) => {
      if (event.target === dialog) {
        closeModal();
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeModal();
      }
    };

    dialog.addEventListener("click", handleOutsideClick);
    window.addEventListener("keydown", handleEscapeKey);

    return () => {
      dialog.removeEventListener("click", handleOutsideClick);
      window.removeEventListener("keydown", handleEscapeKey);
      // document.body.style.overflow = "visible";
      document.removeEventListener("wheel", preventDefault);
      document.removeEventListener("touchmove", preventDefault);
      document.removeEventListener("keydown", handleKeyDown);
      setIsModalOpen(false);
    };
  }, [modalRef]);

  const changeProductsRating = (newRate: number) => {
    setProductReview(newRate);
  };

  const changecustomerServiceRating = (newRate: number) => {
    setClientServiceReview(newRate);
  };

  const isSubmitDisabled = !clientServiceReview || !productReview;

  const handleSubmit = () => {
    setHasSubmitted?.(true);
    sendReview({
      product_image_review: productReview,
      customer_service_review: clientServiceReview,
    });
    closeModal();
  };

  return (
    <dialog
      ref={modalRef}
      className="flex flex-col gap-2 md:grid md:gap-4 p-6 md:p-8 rounded-2xl outline-none shadow-lg"
    >
      <p className="font-bold text-base md:text-xl">{translate("your_opinion")}</p>
      <div className="md:flex gap-6 bg-backgroundGrey p-4 md:p-6 rounded-xl items-center">
        <p>{translate("customer_service")}</p>
        <div className="flex-1" />
        <div className="flex gap-1 xs:pt-2">
          <RatingDiamonds
            id="client_service"
            value={clientServiceReview}
            onChange={(val) => changecustomerServiceRating(val)}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 md:gap-6 bg-backgroundGrey p-4 md:p-6 rounded-xl items-center">
        <p>{translate("product_image_conformity")}</p>
        <div className="md:flex gap-1 md:justify-self-end">
          <RatingDiamonds
            id="product_images"
            value={productReview}
            onChange={(val) => changeProductsRating(val)}
          />
        </div>
      </div>

      <div className="flex gap-4">
        <button
          type="reset"
          className="px-6 py-2 rounded-lg text-sm md:text-base font-semibold bg-white text-redCancel border border-redCancel flex-1 outline-none"
          onClick={closeModal}
        >
          {translate("cancel")}
        </button>
        <button
          type="submit"
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
          className={`px-6 py-2 rounded-lg ${
            isSubmitDisabled ? "bg-gray-100 text-blue" : "bg-blue text-white"
          } text-sm md:text-base font-semibold flex-1 outline-none`}
        >
          {translate("send")}
        </button>
      </div>
    </dialog>
  );
}

export default RatingModal;
